import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import CommonAlert from "./CommonAlertComponent";

interface AlertProps {
  title: string;
  message: string;
  show: boolean;
  button1Text?: string;
  button2Text?: string;
  btn1onPressed?: () => void;
  btn2onPressed?: () => void;
  startAssessmentPressed?: () => void;
  isNeedBackgroundPopUp?: boolean;
  onlyNeedOneButton?: boolean;
  alertType?: | "success"
  | "error"
  | "confirmation"
  | "warning"
  | "info";
  onClose?: () => void;
  identity: string;
  //Warning Popup Props
  multipleLoginProp?: "Session_Ended" | "Session_Detected";
  bottomHintText?: string;
  deviceDetails?: string[];
  btn2close?: boolean;
  eventType: string;
  eventTitle: string;
  eventDesc: string;
  offset: number;
  events: string;
  duration: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;

  status: string;
  number_of_dimension_questions: number;
  dimension_descriptive_questions: number;
  //Program management
  isExpiredProgram?: boolean;
  sessionUrl?: string;
  sessionPass?: string;
  curriculum_list?: any[];
  category_list?: any[];
  eventDuration: string;
  assessment_duration: string;
}

interface AlertContextType {
  showAlert: (props: Partial<AlertProps>) => void;
  hideAlert: () => void;
  isAlertVisible: boolean;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alertProps, setAlertProps] = useState<AlertProps>({
    eventType: "",
    status: "",
    events: "",
    eventTitle: "",
    duration: "",
    identity: "",
    eventDesc: "",
    offset: 0,
    eventDuration: "",
    assessment_duration: "",
    title: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    number_of_dimension_questions: 0,
    dimension_descriptive_questions: 0,
    curriculum_list: [],
    category_list: [],
    message: "",
    show: false,
    button1Text: "Yes",
    button2Text: "No",
    btn1onPressed: () => {},
    btn2onPressed: () => {},
    startAssessmentPressed: () => {},
    isNeedBackgroundPopUp: false,
    onlyNeedOneButton: false,
    alertType: "success",
    bottomHintText: "",
    deviceDetails: [],
    sessionUrl: "",
    sessionPass: "",
    isExpiredProgram: false,

    btn2close: true,

    onClose: () =>
      setAlertProps((prev) => ({
        ...prev,
        show: false,
        deviceDetails: [],
        bottomHintText: "",
      })),
  });

  const showAlert = (newProps: Partial<AlertProps>) => {
    setAlertProps((prev) => ({ ...prev, ...newProps, show: true }));
  };

  // useEffect(()=>{

  // },[showAlert])

  const hideAlert = () => {
    setAlertProps((prev) => ({
      ...prev,
      show: false,
      identity: "",
      deviceDetails: [],
      bottomHintText: "",
    }));
  };

  return (
    <AlertContext.Provider
      value={{ showAlert, hideAlert, isAlertVisible: alertProps.show }}
    >
      {children}
      {alertProps.show && (
        <CommonAlert
          status={alertProps.status}
          title={alertProps.title}
          message={alertProps.message}
          button1Text={alertProps.button1Text}
          button2Text={alertProps.button2Text}
          identity={alertProps.identity}
          multipleLoginProp={alertProps.multipleLoginProp}
          btn1onPressed={() => {
            if (alertProps.btn1onPressed) {
              alertProps.btn1onPressed();
            }
            hideAlert(); // Ensure the alert is hidden
          }}
          btn2onPressed={() => {
            if (alertProps.btn2onPressed) {
              alertProps.btn2onPressed();
            }
            if (alertProps.btn2close === true) {
              hideAlert(); // Ensure the alert is hidden
            }
          }}
          startAssessmentPressed={() => {
            if (alertProps.startAssessmentPressed) {
              alertProps.startAssessmentPressed();
            }
            hideAlert(); // Ensure the alert is hidden
          }}
          isNeedBackgroundPopUp={alertProps.isNeedBackgroundPopUp}
          onlyNeedOneButton={alertProps.onlyNeedOneButton}
          alertType={alertProps.alertType}
          show={alertProps.show}
          onClose={() => hideAlert()} // Ensure onClose hides the alert
          bottomHintText={alertProps.bottomHintText}
          deviceDetails={alertProps.deviceDetails}
          category_list={alertProps.category_list}
          sessionUrl={alertProps.sessionUrl}
          sessionPass={alertProps.sessionPass}
          eventType={alertProps.eventType}
          eventTitle={alertProps.eventTitle}
          events={alertProps.events}
          duration={alertProps.duration}
          eventDesc={alertProps.eventDesc}
          offset={alertProps.offset}
          start_date={alertProps.start_date}
          end_date={alertProps.end_date}
          end_time={alertProps.end_time}
          start_time={alertProps.start_time}
          eventDuration={alertProps.eventDuration}
          assessment_duration={alertProps.assessment_duration}
          number_of_dimension_questions={
            alertProps.number_of_dimension_questions
          }
          dimension_descriptive_questions={
            alertProps.dimension_descriptive_questions
          }
          curriculum_list={alertProps.curriculum_list}
        />
      )}
    </AlertContext.Provider>
  );
};
